// Consent Mode V2 Management Utility
import Cookies from 'js-cookie';

// Consent categories for Google Consent Mode V2
export const CONSENT_CATEGORIES = {
  AD_STORAGE: 'ad_storage',
  AD_USER_DATA: 'ad_user_data', 
  AD_PERSONALIZATION: 'ad_personalization',
  ANALYTICS_STORAGE: 'analytics_storage',
  FUNCTIONALITY_STORAGE: 'functionality_storage',
  PERSONALIZATION_STORAGE: 'personalization_storage',
  SECURITY_STORAGE: 'security_storage'
};

// Default consent state (all denied except security)
export const DEFAULT_CONSENT_STATE = {
  [CONSENT_CATEGORIES.AD_STORAGE]: 'denied',
  [CONSENT_CATEGORIES.AD_USER_DATA]: 'denied',
  [CONSENT_CATEGORIES.AD_PERSONALIZATION]: 'denied',
  [CONSENT_CATEGORIES.ANALYTICS_STORAGE]: 'denied',
  [CONSENT_CATEGORIES.FUNCTIONALITY_STORAGE]: 'denied',
  [CONSENT_CATEGORIES.PERSONALIZATION_STORAGE]: 'denied',
  [CONSENT_CATEGORIES.SECURITY_STORAGE]: 'granted'
};

// Essential-only consent state
export const ESSENTIAL_CONSENT_STATE = {
  ...DEFAULT_CONSENT_STATE
};

// All granted consent state
export const ALL_GRANTED_CONSENT_STATE = {
  [CONSENT_CATEGORIES.AD_STORAGE]: 'granted',
  [CONSENT_CATEGORIES.AD_USER_DATA]: 'granted',
  [CONSENT_CATEGORIES.AD_PERSONALIZATION]: 'granted',
  [CONSENT_CATEGORIES.ANALYTICS_STORAGE]: 'granted',
  [CONSENT_CATEGORIES.FUNCTIONALITY_STORAGE]: 'granted',
  [CONSENT_CATEGORIES.PERSONALIZATION_STORAGE]: 'granted',
  [CONSENT_CATEGORIES.SECURITY_STORAGE]: 'granted'
};

// Crawler consent state (analytics enabled, ads disabled)
export const CRAWLER_CONSENT_STATE = {
  [CONSENT_CATEGORIES.AD_STORAGE]: 'denied',
  [CONSENT_CATEGORIES.AD_USER_DATA]: 'denied',
  [CONSENT_CATEGORIES.AD_PERSONALIZATION]: 'denied',
  [CONSENT_CATEGORIES.ANALYTICS_STORAGE]: 'granted',
  [CONSENT_CATEGORIES.FUNCTIONALITY_STORAGE]: 'granted',
  [CONSENT_CATEGORIES.PERSONALIZATION_STORAGE]: 'denied',
  [CONSENT_CATEGORIES.SECURITY_STORAGE]: 'granted'
};

// Cookie storage keys
const CONSENT_COOKIE_KEY = 'gamechat-consent-v2';
const CONSENT_VERSION = '2.0';

// Get stored consent state
export const getStoredConsent = () => {
  try {
    const stored = Cookies.get(CONSENT_COOKIE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate stored consent has correct structure
      if (parsed.version === CONSENT_VERSION && parsed.state) {
        return parsed.state;
      }
    }
  } catch (error) {
    console.warn('Error parsing stored consent:', error);
  }
  return null;
};

// Store consent state
export const storeConsent = (consentState) => {
  const consentData = {
    version: CONSENT_VERSION,
    state: consentState,
    timestamp: new Date().toISOString()
  };
  
  Cookies.set(CONSENT_COOKIE_KEY, JSON.stringify(consentData), { 
    expires: 150, // 5 months
    secure: true,
    sameSite: 'strict'
  });
};

// Update Google Consent Mode
export const updateConsentMode = (consentState) => {
  if (window.gtag) {
    window.gtag('consent', 'update', consentState);
    console.log('Consent Mode updated:', consentState);
  } else {
    console.warn('gtag not available for consent update');
  }
};

// Initialize consent state
export const initializeConsent = () => {
  const stored = getStoredConsent();
  if (stored) {
    updateConsentMode(stored);
    return stored;
  }
  
  // No stored consent, use default
  updateConsentMode(DEFAULT_CONSENT_STATE);
  return DEFAULT_CONSENT_STATE;
};

// Grant all consent
export const grantAllConsent = () => {
  updateConsentMode(ALL_GRANTED_CONSENT_STATE);
  storeConsent(ALL_GRANTED_CONSENT_STATE);
  return ALL_GRANTED_CONSENT_STATE;
};

// Grant essential only consent
export const grantEssentialConsent = () => {
  updateConsentMode(ESSENTIAL_CONSENT_STATE);
  storeConsent(ESSENTIAL_CONSENT_STATE);
  return ESSENTIAL_CONSENT_STATE;
};

// Grant crawler consent
export const grantCrawlerConsent = () => {
  updateConsentMode(CRAWLER_CONSENT_STATE);
  // Don't store crawler consent in cookies
  return CRAWLER_CONSENT_STATE;
};

// Check if analytics is enabled
export const isAnalyticsEnabled = (consentState) => {
  return consentState?.[CONSENT_CATEGORIES.ANALYTICS_STORAGE] === 'granted';
};

// Check if advertising is enabled
export const isAdvertisingEnabled = (consentState) => {
  return consentState?.[CONSENT_CATEGORIES.AD_STORAGE] === 'granted';
};

// Check if personalization is enabled
export const isPersonalizationEnabled = (consentState) => {
  return consentState?.[CONSENT_CATEGORIES.PERSONALIZATION_STORAGE] === 'granted';
};

// Get consent summary for display
export const getConsentSummary = (consentState) => {
  return {
    analytics: isAnalyticsEnabled(consentState),
    advertising: isAdvertisingEnabled(consentState),
    personalization: isPersonalizationEnabled(consentState),
    functionality: consentState?.[CONSENT_CATEGORIES.FUNCTIONALITY_STORAGE] === 'granted',
    security: consentState?.[CONSENT_CATEGORIES.SECURITY_STORAGE] === 'granted'
  };
};

// Clear stored consent
export const clearConsent = () => {
  Cookies.remove(CONSENT_COOKIE_KEY);
  updateConsentMode(DEFAULT_CONSENT_STATE);
}; 